/**
 * UpcomingSessionsCard — self-serve panel on /dashboard.
 *
 *  • Shows the parent/member's recurring weekly cadence ("Tue / Thu @ 4 PM").
 *  • Lists their next 5 upcoming bookings (date, time, athlete).
 *  • Two CTAs: "Book another session" + "View full schedule".
 *
 * Athlete-mode users see their own slim version of this — see AthleteHome.
 * Reuses /api/me/recurring-template + /api/schedule/my-bookings.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, Repeat, ChevronRight, Loader2, X, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function UpcomingSessionsCard() {
    const [bookings, setBookings] = useState([]);
    const [template, setTemplate] = useState(null);
    const [busy, setBusy] = useState(true);
    const [cancelling, setCancelling] = useState(null);
    const [rescheduleFor, setRescheduleFor] = useState(null);

    const load = async () => {
        try {
            const [b, t] = await Promise.all([
                axios.get(`${API}/schedule/my-bookings`, { withCredentials: true }).catch(() => ({ data: [] })),
                axios.get(`${API}/me/recurring-template`, { withCredentials: true }).catch(() => ({ data: null })),
            ]);
            setBookings(Array.isArray(b.data) ? b.data : (b.data?.bookings || []));
            setTemplate(t.data || null);
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleCancel = async (b) => {
        const label = `${new Date(b.slot_start || b.date).toLocaleString()} (${b.athlete_name || "Session"})`;
        // Model B cutoffs: >12h free · 1h-12h late cancel · <1h blocked.
        let prompt = `Cancel ${label}? Your credit will be refunded.`;
        let hoursOut = null;
        try {
            const startMs = new Date(b.slot_start || b.date).getTime();
            hoursOut = (startMs - Date.now()) / 3.6e6;
            if (hoursOut < 1) {
                prompt = (
                    `❌ Too late to cancel ${label}.\n\n` +
                    "Sessions can't be cancelled within 1 hour of start time.\n" +
                    "Contact your coach — they'll mark it as a no-show if you can't make it."
                );
                window.alert(prompt);
                return;
            }
            if (hoursOut < 12) {
                prompt = (
                    `⚠ LATE CANCEL: ${label} starts in ${Math.max(0, hoursOut).toFixed(1)}h.\n` +
                    "Policy: 2 late cancels in a calendar month = 1 paid session deducted.\n\n" +
                    "Cancel anyway?"
                );
            }
        } catch { /* ignore */ }
        if (!window.confirm(prompt)) return;
        setCancelling(b.booking_id);
        try {
            const r = await axios.delete(`${API}/schedule/bookings/${b.booking_id}`, { withCredentials: true });
            const msg = r.data?.message || "";
            if (r.data?.late_cancel) {
                if (r.data?.penalty?.penalised) {
                    toast.error("Late cancel — 1 session deducted", { description: msg, duration: 8000 });
                } else {
                    toast.warning("Late cancel recorded", { description: msg, duration: 7000 });
                }
            } else {
                toast.success("Cancelled — credit refunded", { description: msg });
            }
            setRescheduleFor(b);
            await load();
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 409) {
                toast.error("Too late to cancel", { description: detail, duration: 9000 });
            } else {
                toast.error("Couldn't cancel", { description: detail });
            }
        } finally {
            setCancelling(null);
        }
    };

    if (busy) {
        return (
            <div className="border border-zinc-800 rounded-2xl bg-zinc-950/60 p-6 flex items-center gap-2 text-zinc-300 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading your schedule…
            </div>
        );
    }

    const now = new Date();
    const upcoming = (bookings || [])
        .filter((b) => b.status === "booked" && new Date(b.slot_start || b.date) >= now)
        .sort((a, b) => new Date(a.slot_start || a.date) - new Date(b.slot_start || b.date))
        .slice(0, 5);

    const cadenceDays = (template?.days || []).map((d) => DAY_LABELS[d]).join(" · ");
    const cadenceTime = template?.start_time || "";

    return (
        <div data-testid="upcoming-sessions-card" className="border border-zinc-800 rounded-2xl bg-zinc-950/60 overflow-hidden">
            <div className="px-6 py-5 border-b border-zinc-800 flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red inline-flex items-center gap-2">
                        <Calendar className="w-3 h-3" /> Your schedule
                    </div>
                    <h3 className="font-display text-2xl tracking-wider mt-1">UP NEXT</h3>
                </div>
                <Link
                    to="/dashboard/schedule"
                    data-testid="view-full-schedule"
                    className="text-[11px] tracking-[0.15em] uppercase text-zinc-200 hover:text-brand-red inline-flex items-center gap-1"
                >
                    View full schedule <ChevronRight className="w-3 h-3" />
                </Link>
            </div>

            {template && (cadenceDays || cadenceTime) && (
                <div className="px-6 py-3 border-b border-zinc-800 flex items-center gap-3 bg-zinc-900/30">
                    <Repeat className="w-4 h-4 text-brand-navy-300" />
                    <div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Standing cadence</div>
                        <div className="text-sm text-white">
                            {cadenceDays || "—"}{cadenceTime ? ` @ ${cadenceTime}` : ""}
                        </div>
                    </div>
                </div>
            )}

            <div className="px-6 py-5">
                {upcoming.length === 0 ? (
                    <div className="text-sm text-zinc-300 text-center py-4">
                        Nothing on the books yet.{" "}
                        <Link to="/dashboard/schedule" className="text-brand-red hover:underline">Book a session →</Link>
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {upcoming.map((b) => {
                            const start = new Date(b.slot_start || b.date);
                            const dayLabel = start.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
                            const timeLabel = start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
                            return (
                                <li
                                    key={b.booking_id}
                                    data-testid={`upcoming-booking-${b.booking_id}`}
                                    className="border border-zinc-800 rounded-lg bg-black/30 px-4 py-3 flex items-center justify-between gap-3"
                                >
                                    <div className="min-w-0">
                                        <div className="text-sm text-white truncate">
                                            {b.athlete_name || "Session"}
                                        </div>
                                        <div className="text-[11px] text-zinc-400">
                                            {dayLabel} · {timeLabel}
                                            {b.duration_minutes ? ` · ${b.duration_minutes} min` : ""}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <div className="text-[10px] tracking-[0.15em] uppercase text-brand-navy-300 border border-brand-navy-700 px-2 py-0.5">
                                            booked
                                        </div>
                                        <button
                                            type="button"
                                            data-testid={`cancel-booking-${b.booking_id}`}
                                            onClick={() => handleCancel(b)}
                                            disabled={cancelling === b.booking_id}
                                            title="Cancel"
                                            className="p-1.5 text-zinc-400 hover:text-brand-red disabled:opacity-50"
                                        >
                                            {cancelling === b.booking_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
                <div className="mt-4 pt-4 border-t border-zinc-800 flex justify-between">
                    <Link
                        to="/dashboard/schedule"
                        data-testid="book-another-session"
                        className="btn-tactical text-xs"
                    >
                        Book a session
                    </Link>
                </div>
            </div>
            {rescheduleFor && (
                <RescheduleRequestModal
                    booking={rescheduleFor}
                    onClose={() => setRescheduleFor(null)}
                />
            )}
        </div>
    );
}

function RescheduleRequestModal({ booking, onClose }) {
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);

    const send = async () => {
        if (!body.trim()) { onClose(); return; }
        setSending(true);
        try {
            await axios.post(
                `${API}/me/scheduling-request`,
                { booking_id: booking.booking_id, body },
                { withCredentials: true },
            );
            toast.success("Request sent to coach");
            onClose();
        } catch (e) {
            toast.error("Couldn't send", { description: e?.response?.data?.detail });
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="reschedule-modal" className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden">
                <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">Reschedule</div>
                        <h4 className="font-display text-xl tracking-wider mt-0.5">Need a different time?</h4>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white">✕</button>
                </div>
                <div className="p-5 space-y-3">
                    <p className="text-sm text-zinc-300">
                        Send a quick note to your coach — let us know what works and we'll find you a spot. (Goes into a dedicated scheduling thread, not buried in general messages.)
                    </p>
                    <textarea
                        data-testid="reschedule-body"
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        rows={4}
                        placeholder="e.g. Can we move to Wed 5pm? Or any time Thursday after 3?"
                        className="w-full bg-black border border-zinc-700 rounded-lg p-2 text-sm text-white focus:border-brand-red focus:outline-none"
                    />
                </div>
                <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2">
                    <button type="button" data-testid="reschedule-skip" onClick={onClose} className="text-xs text-zinc-400 hover:text-white px-3 py-2">No thanks</button>
                    <button
                        type="button"
                        data-testid="reschedule-send"
                        onClick={send}
                        disabled={sending || !body.trim()}
                        className="btn-tactical text-xs disabled:opacity-50"
                    >
                        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
                        Send to coach
                    </button>
                </div>
            </div>
        </div>
    );
}
